/**
 * What the user reads when a message key cannot be resolved.
 *
 * next-intl's built-in fallback is `joinPath(namespace, key)` — see
 * `defaultGetMessageFallback` in `use-intl`. That prints
 * `settings.treeNavDefsOrg.telefonie` into a menu where a word belongs, which
 * is worse than any wrong word: it reads as a broken app.
 *
 * A gap can open for reasons no catalog check can rule out at runtime — a dev
 * server serving a stale bundle while a JSON is being edited, a client chunk
 * older than the deploy that added the key. `scripts/check-i18n.mjs` still
 * fails the build for a missing translation; that rule does not soften. This
 * only decides what is rendered in the moment a gap exists.
 *
 * Pure leaf: no imports, so both the server config and the client provider can
 * use the exact same function.
 */

/** Splits camelCase, snake_case and kebab-case into separate words. */
function splitIntoWords(segment: string): string[] {
  return segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 0);
}

/**
 * Turns a message key into a readable word.
 *
 * Only the last dot segment is used: the namespace names the screen, not the
 * label, so `settings.treeNavDefsOrg.telefonie` has to read "Telefonie".
 */
export function humanizeMessageKey(props: {
  readonly namespace?: string;
  readonly key: string;
}): string {
  const lastSegment: string = props.key.split(".").filter(Boolean).pop() ?? "";
  const words: string[] = splitIntoWords(lastSegment);
  if (words.length === 0) return "…";

  const [first = "", ...rest] = words;
  const capitalized: string = first.charAt(0).toUpperCase() + first.slice(1);
  return [capitalized, ...rest.map((word) => word.toLowerCase())].join(" ");
}
